import {
  getLanguageFlag,
  getLanguageName,
  supportedLanguages,
  topLanguages as defaultTopLanguages,
  LocalStorageKeys,
  languageSearchAliases,
} from "../../types";
import classNames from "./LanguageMultiSelect.module.css";
import {
  CSSProperties,
  forwardRef,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Popover from "@radix-ui/react-popover";
import { IoChevronDown, IoChevronUp, IoClose, IoSearch } from "react-icons/io5";

const Language = (props: {
  type: "single" | "multi";
  topLanguage?: boolean;
  language: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) => {
  if (props.topLanguage) {
    return (
      <button
        type="button"
        disabled={props.disabled}
        onClick={!props.disabled ? props.onClick : undefined}
        aria-selected={props.selected}
      >
        {`${getLanguageFlag(props.language)} ${getLanguageName(
          props.language
        )}`}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={!props.disabled ? props.onClick : undefined}
      disabled={props.disabled}
    >
      <input
        type={props.type === "single" ? "radio" : "checkbox"}
        disabled={props.disabled}
        checked={props.selected}
        readOnly={true}
      />
      {`${getLanguageFlag(props.language)} ${getLanguageName(props.language)}`}
    </button>
  );
};

export const Languages = forwardRef(
  (
    props: {
      type: "multi" | "single";
      languages: string[];
      selectedLanguages: string[];
      onSelectLanguage: (language: string) => void;
      onClose: () => void;
      style?: CSSProperties;
      disableUnselected?: boolean;
    },
    forwardedRef: React.ForwardedRef<HTMLDivElement>
  ) => {
    const [topLanguages, setTopLanguages] = useState<string[]>(
      localStorage.getItem(LocalStorageKeys.TOP_LANGUAGES)?.split(",") ||
        defaultTopLanguages.filter((l) => l !== "autodetect")
    );

    useEffect(() => {
      localStorage.setItem(
        LocalStorageKeys.TOP_LANGUAGES,
        topLanguages.join(",")
      );
    }, [topLanguages]);

    const [query, setQuery] = useState("");

    return (
      <Popover.Content
        ref={forwardedRef}
        className={classNames.Content}
        align="start"
        side="bottom"
        style={props.style}
        sideOffset={8}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {props.languages.length >= 10 && (
          <>
            <div className={classNames.Header}>
              <div className={classNames.Search}>
                <IoSearch />
                <input
                  type="text"
                  placeholder="Search language"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <button onClick={props.onClose} type="button">
                <IoClose />
              </button>
            </div>
            <div className={classNames.TopLanguages}>
              <label>Top languages</label>
              <div>
                {topLanguages
                  .filter((l) => props.languages.includes(l))
                  .filter(
                    (l) =>
                      !query.length ||
                      getLanguageName(l)?.toLowerCase().includes(query) ||
                      languageSearchAliases[l]?.some((alias) =>
                        alias.toLowerCase().includes(query.toLowerCase())
                      )
                  )
                  .map((language, index) => (
                    <Language
                      topLanguage={true}
                      key={language + index}
                      language={language}
                      selected={props.selectedLanguages.includes(language)}
                      disabled={
                        !props.selectedLanguages.includes(language) &&
                        !!props.disableUnselected
                      }
                      type={props.type}
                      onClick={() => {
                        props.onSelectLanguage(language);
                      }}
                    />
                  ))}
              </div>
            </div>
          </>
        )}
        <div className={classNames.AllLanguages}>
          {props.languages.length >= 10 && <label>All languages</label>}
          {props.languages
            .filter(
              (l) =>
                !query.length ||
                getLanguageName(l)?.toLowerCase().includes(query) ||
                languageSearchAliases[l]?.some((alias) =>
                  alias.toLowerCase().includes(query.toLowerCase())
                )
            )
            .map((language) => (
              <Language
                key={language}
                language={language}
                selected={props.selectedLanguages.includes(language)}
                disabled={
                  !props.selectedLanguages.includes(language) &&
                  !!props.disableUnselected
                }
                type={props.type}
                onClick={() => {
                  props.onSelectLanguage(language);
                  if (!props.selectedLanguages.includes(language)) {
                    setTopLanguages((prevState) => [
                      language,
                      ...prevState
                        .filter((l) => l !== language)
                        .slice(0, prevState.length >= 10 ? -1 : undefined),
                    ]);
                  }
                }}
              />
            ))}
        </div>
      </Popover.Content>
    );
  }
);

const LanguageMultiSelect = (props: {
  selectedLanguages: string[];
  onChange: (selectedLanguages: string[]) => void;
  maxLanguages?: number;
  trigger?: ReactNode;
}) => {
  const canAddMoreLanguages = useMemo(() => {
    return (
      !props.maxLanguages || props.selectedLanguages.length < props.maxLanguages
    );
  }, [props.maxLanguages, props.selectedLanguages]);

  const languages = useMemo(() => {
    return Object.values(supportedLanguages).flatMap((languages) =>
      Array.from(languages)
    );
  }, []);

  const [isOpen, setIsOpen] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>();

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, []);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild ref={triggerRef}>
        {props.trigger ??
          (!props.selectedLanguages.length ? (
            <button className={classNames.Trigger} type="button">
              Add language
              {!isOpen ? <IoChevronDown /> : <IoChevronUp />}
            </button>
          ) : (
            <button className={classNames.Trigger} type="button">
              <div className={classNames.Languages}>
                {props.selectedLanguages.map((language) => (
                  <div key={language}>{`${getLanguageFlag(
                    language
                  )} ${getLanguageName(language)}`}</div>
                ))}
              </div>
              {!isOpen ? <IoChevronDown /> : <IoChevronUp />}
            </button>
          ))}
      </Popover.Trigger>
      <Popover.Portal>
        <Languages
          type="multi"
          languages={languages}
          style={{ width: triggerWidth }}
          selectedLanguages={props.selectedLanguages}
          onSelectLanguage={(language) => {
            if (props.selectedLanguages.includes(language)) {
              props.onChange(
                props.selectedLanguages.filter((l) => l !== language)
              );
            } else if (canAddMoreLanguages) {
              props.onChange([...props.selectedLanguages, language]);
            }
          }}
          disableUnselected={!canAddMoreLanguages}
          onClose={() => setIsOpen(false)}
        />
      </Popover.Portal>
    </Popover.Root>
  );
};

export default LanguageMultiSelect;
